import { useNavigate } from 'react-router-dom';
import { AffiliateDisclosure } from '@/components/AffiliateFooter';

export default function PrivacyPolicyPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-gray-300" data-testid="privacy-policy-page">
      <div className="max-w-3xl mx-auto px-6 py-16">
        <button onClick={() => navigate(-1)} className="text-gray-400 hover:text-white mb-8 text-sm">&larr; Back</button>
        <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: "'Manrope', sans-serif" }}>Privacy Policy</h1>
        <p className="text-gray-500 mb-8 text-sm">Last updated: March 16, 2026</p>

        <div className="space-y-6 text-sm leading-relaxed">
          <section>
            <h2 className="text-lg font-semibold text-white mb-2">1. Introduction</h2>
            <p>Brand OS ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our web application and services. Brand OS is operated by a Delaware C Corporation with its registered agent at LegalInc, 651 N. Broad St. Suite 201, Middletown, DE 19709.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">2. Information We Collect</h2>
            <p><strong>Account Information:</strong> When you register, we collect your name, email address, and password (stored in hashed form).</p>
            <p><strong>Profile Data:</strong> Information you provide during onboarding including personal improvement goals, routines, supplement plans, and preferences across our seven categories.</p>
            <p><strong>Photos:</strong> If you upload progress photos, these are stored securely and only accessible to your account.</p>
            <p><strong>Payment Information:</strong> Payment processing is handled by Stripe, Inc. We do not store your credit card details. We retain transaction records (amount, date, plan type).</p>
            <p><strong>Usage Data:</strong> We collect information about how you interact with the app, including task completions, feature usage, and session data.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">3. How We Use Your Information</h2>
            <p>We use your information to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Provide and personalize our AI-powered improvement recommendations</li>
              <li>Generate daily tasks tailored to your goals</li>
              <li>Process payments and manage subscriptions</li>
              <li>Send product updates and communications (you can unsubscribe at any time via your email provider)</li>
              <li>Improve our services and develop new features</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">4. Data Sharing & Disclosure</h2>
            <p>We do not sell your personal information to third parties. We may share data with:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Service Providers:</strong> Stripe (payments), MongoDB (database hosting), OpenAI (AI features) — these providers process data on our behalf under strict confidentiality agreements</li>
              <li><strong>Affiliate Partners:</strong> When you click affiliate links on our site, Amazon.com may place cookies in your browser per their own privacy policy. We do not share your personal information directly with Amazon. See Section 8 for details.</li>
              <li><strong>Legal Requirements:</strong> When required by law, subpoena, or legal process</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">5. Data Security</h2>
            <p>We implement industry-standard security measures including encrypted data transmission (HTTPS/TLS), hashed passwords (bcrypt), and secure authentication tokens (JWT). However, no method of electronic storage is 100% secure.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">6. Your Rights</h2>
            <p><strong>United States (CAN-SPAM Act, CCPA):</strong> You have the right to opt out of commercial emails, request access to your personal data, request deletion of your data, and not be discriminated against for exercising your rights.</p>
            <p><strong>Canada (CASL, PIPEDA):</strong> You have the right to access your personal information, request corrections, withdraw consent for communications, and file a complaint with the Office of the Privacy Commissioner of Canada.</p>
            <p>To exercise any of these rights, contact us at support@buildbrandos.com.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">7. Data Retention</h2>
            <p>We retain your data for as long as your account is active. Upon account deletion request, we will remove your personal data within 30 days, except where retention is required by law.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">8. Cookies, Tracking & Affiliate Links</h2>
            <p>We use local storage for authentication tokens and user preferences. We do not use third-party tracking cookies for advertising purposes.</p>
            <p className="mt-2"><strong>Amazon Affiliate Tracking:</strong> Brand OS participates in the Amazon Services LLC Associates Program. When you click on Amazon affiliate links on our website, Amazon.com may place a tracking cookie in your browser to attribute qualifying purchases. This cookie is governed by Amazon's privacy policy, not ours. We do not have access to or control over Amazon's cookies.</p>
            <p className="mt-2">Key details about affiliate link tracking:</p>
            <ul className="list-disc pl-6 space-y-1 mt-1">
              <li>Amazon's affiliate tracking cookie typically lasts 24 hours from the time of click</li>
              <li>We do not collect, store, or have access to your Amazon purchase history</li>
              <li>Clicking an affiliate link does not share any of your Brand OS account information with Amazon</li>
              <li>You can manage cookie preferences in your browser settings at any time</li>
              <li>Our affiliate tag (buildbrandos-20) is included in Amazon links per the Associates Operating Agreement</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">9. Children's Privacy</h2>
            <p>Our services are not directed to individuals under the age of 16. We do not knowingly collect personal information from children.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">10. Changes to This Policy</h2>
            <p>We may update this Privacy Policy from time to time. We will notify you of material changes by email or through the app.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-2">11. Contact Us</h2>
            <p>For questions about this Privacy Policy or to exercise your data rights:</p>
            <p className="mt-2">
              Brand OS<br />
              Email: support@buildbrandos.com<br />
              Registered Agent: LegalInc, 651 N. Broad St. Suite 201, Middletown, DE 19709
            </p>
          </section>
        </div>

        <div className="mt-12 pt-6 border-t border-white/10">
          <AffiliateDisclosure className="text-gray-500" />
        </div>
      </div>
    </div>
  );
}
